import styled from "styled-components";

export const PageWrapper = styled.div`
    background: #fff;
    /* No top padding: hero is first; top padding was a white gap under the app header */
    padding: 0 40px 40px;
    box-sizing: border-box;
    min-width: 0;
    overflow-x: hidden;
`;

/* Full-bleed past PageWrapper horizontal padding (avoid 100vw, which widens past the main column) */
const fullBleedInPaddedPage = `
    width: calc(100% + 80px);
    margin-left: -40px;
    margin-right: -40px;
    box-sizing: border-box;
`;

/* Full-bleed hero; background layer is absolute (cover) */
export const HeroSection = styled.section`
    ${fullBleedInPaddedPage}
    position: relative;
    overflow: hidden;
    isolation: isolate;
    min-width: 0;
    min-height: 60vh;
    background-color: #051C2C;
    display: flex;
    align-items: center;
`;

/* Wrapper for absolute-positioned hero background */
export const HeroVideoContainer = styled.div`
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    overflow: hidden;
`;

/* Static hero art — wide panoramic; keep particles visible on the right */
export const HeroBackgroundImage = styled.div`
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-image: url(${({ $url }) => $url});
    background-size: cover;
    background-position: right center;
    background-repeat: no-repeat;
    background-color: #051c2c;
`;

/* Match msa-frontend-eco TrendsScreenHeader HeaderContentContainer; above background */
export const HeroInner = styled.div`
    position: relative;
    z-index: 1;
    margin: 0 auto;
    width: 100%;
    min-width: 0;
    padding: 40px 10%;
    box-sizing: border-box;
`;
/* Match eco HeaderTitle.
   Each line is wrapped in a span and locked with `white-space: nowrap` so
   "Welcome to Performance Lens" / "Global Growth Cube" stay on their own
   lines at every browser zoom level (zoom scales fonts and the percentage
   width together, so the relative layout is preserved). */
export const HeroTitle = styled.div`
    font-size: 48px;
    margin: 5px 0;
    font-family: 'Bower', serif;
    color: #ffffff;
    line-height: 60px;
    font-weight: 500;
    width: auto;
    max-width: 100%;

    & > span {
        display: block;
        white-space: nowrap;
        /* Some global resets / MDS styles target span and reset typography.
           Force the spans to inherit the HeroTitle font so the 48px Bower
           styling still applies after wrapping each line in a span. */
        font: inherit;
        font-family: inherit;
        font-size: inherit;
        font-weight: inherit;
        line-height: inherit;
        color: inherit;
        letter-spacing: inherit;
    }
`;
/* Match eco HeaderSubtitle */
export const HeroSubtext = styled.div`
    color: #ffffff;
    line-height: 24px;
    opacity: 0.7;
    font-size: 16px;
    font-family: 'McKinsey Sans';
    width: 40%;
`;
/* Match eco HeaderMaintitle (e.g. “Hi, {name}”) */
export const HeroWelcome = styled.div`
    color: #ffffff;
    line-height: 36px;
    opacity: 0.7;
    font-weight: 300;
    font-size: 20px;
    font-family: 'McKinsey Sans';
    width: 40%;
`;

export const Section = styled.section`
    padding: 48px;
    max-width: 1200px;
    margin: 0 auto;
`;

export const SectionTitle = styled.div`
    color: #000000;
    font-family: McKinsey Sans;
    font-size: 28px;
    font-weight:  500;
    line-height: 32px;
    margin-bottom: 16px;
`;

export const OfferingsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

export const MetricsSection = styled.section`
    background: linear-gradient(122.86deg, #051D2C 45.43%, #2251FF 123.25%);
    padding: 48px;
    display: flex;
    justify-content: center;
    gap: 80px;
    flex-wrap: wrap;
`;

export const MetricItem = styled.div`
    text-align: center;
    color: #FFFFFF;
    .metricValue {
        font-size: 36px;
        font-weight: 700;
        margin-bottom: 8px;
    }
    .metricLabel {
        font-size: 14px;
        font-weight: 300;
    }
`;

export const BenefitsGrid = styled.div`
display: grid;
grid-template-columns: repeat(3, 1fr);
gap: 24px;
@media (max-width: 900px) {
    grid-template-columns: 1fr;
}
`;

export const BenefitCard = styled.div`
padding: 24px;
background: #fff;
border: 1px solid #e0e0e0;
border-radius: 4px;
.benifitTitle {
    font-size: 16px;
    font-weight: 700;
    color: #333;
    margin: 0 0 12px 0;
}
.benifitDescription {
    font-size: 14px;
    color: #6c6c6c;
    line-height: 1.5;
    margin: 0;
}
`;

export const WhyGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

export const WhyCard = styled.div`
    padding: 24px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    .whyTitle {
        font-size: 16px;
        font-weight: 700;
        color: #333;
        margin: 0 0 12px 0;
    }
    .whyDescription {
        font-size: 14px;
        color: #6c6c6c;
        line-height: 1.5;
        margin: 0;
    }
`;

/* Responsive side padding: 112px both sides is too wide on narrow viewports and can add horizontal scroll */
export const HearFromYouSection = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 56px 24px;
  @media (min-width: 900px) {
    padding: 56px 64px;
  }
  @media (min-width: 1200px) {
    padding: 56px 112px;
  }
`;

export const HearFromYouInner = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 48px 48px 48px 40px;
`;

/* ~25% smaller than SectionTitle / body copy in this block */
export const HearFromYouTitle = styled.div`
    color: #000000;
    font-family: McKinsey Sans;
    font-size: 21px;
    font-weight: 500;
    line-height: 24px;
    margin-bottom: 12px;
`;

export const HearFromYouText = styled.p`
    font-size: 16px;
    font-family: McKinsey Sans;
    color: #000000;
    line-height: 21px;
    font-weight: 400;
    a {
        color: #2251ff;
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }
`;

/* Full-bleed footer band (same breakout as hero — avoids 100vw overflow) */
export const FooterArea = styled.div`
    background: #fff;
    ${fullBleedInPaddedPage}
    box-sizing: border-box;
    position: relative;
`;

export const FooterBlock = styled.div`
    margin: 0;
    padding: 0 48px;
    width: 100%;
    box-sizing: border-box;
    overflow-x: visible;
    & > div:first-of-type {
        border-top: none;
    }
`;
